import type { ParticleSystem } from "@/types";
import { useEffect, useState, type FC, type RefObject } from "react";
import { InstancedBufferAttribute, InstancedMesh } from "three";
import { generateSphereEmitter } from "./helpers/generateSphereEmitter";

type EmitterProps = {
  config: ParticleSystem;
  meshRef: RefObject<InstancedMesh>;
};

/**
 * The emitter is responsible for creating all particles upfront. The data is
 * then passed to the shader to handle the visuals over each particle's lifespan.
 */
export const Emitter: FC<EmitterProps> = ({ config, meshRef }) => {
  const [sphere] = useState(() => generateSphereEmitter(config));

  // TODO: Can we optimize by not passing in unused attributes?
  useEffect(() => {
    if (!meshRef.current) return;

    const geometry = meshRef.current.geometry;

    // Pack timing and size data: (index, emitTime, lifetime, size)
    const timingAndSize = new Float32Array(config.capacity * 4);
    for (let i = 0; i < config.capacity; i++) {
      const i4 = i * 4;
      timingAndSize[i4] = i; // index
      timingAndSize[i4 + 1] = sphere.emitTimes[i]; // emitTime
      timingAndSize[i4 + 2] = sphere.lifetimes[i]; // lifetime
      timingAndSize[i4 + 3] = sphere.sizes[i]; // size
    }
    geometry.setAttribute(
      "aTimingAndSize",
      new InstancedBufferAttribute(timingAndSize, 4),
    );

    // Pack motion data: (direction.xyz, angularSpeed)
    const motion = new Float32Array(config.capacity * 4);
    for (let i = 0; i < config.capacity; i++) {
      const i3 = i * 3;
      const i4 = i * 4;
      motion[i4] = sphere.directions[i3]; // direction.x
      motion[i4 + 1] = sphere.directions[i3 + 1]; // direction.y
      motion[i4 + 2] = sphere.directions[i3 + 2]; // direction.z
      motion[i4 + 3] = sphere.angularSpeeds[i]; // angularSpeed
    }
    geometry.setAttribute("aMotion", new InstancedBufferAttribute(motion, 4));

    // Pack position and rotation data: (position.xyz, zRotation)
    const positionAndRotation = new Float32Array(config.capacity * 4);
    for (let i = 0; i < config.capacity; i++) {
      const i3 = i * 3;
      const i4 = i * 4;
      positionAndRotation[i4] = sphere.positions[i3]; // position.x
      positionAndRotation[i4 + 1] = sphere.positions[i3 + 1]; // position.y
      positionAndRotation[i4 + 2] = sphere.positions[i3 + 2]; // position.z
      positionAndRotation[i4 + 3] = sphere.zRotations[i]; // zRotation
    }
    geometry.setAttribute(
      "aPosAndRotation",
      new InstancedBufferAttribute(positionAndRotation, 4),
    );

    geometry.setAttribute(
      "instanceColor",
      new InstancedBufferAttribute(sphere.colors, 4),
    );
  }, [meshRef, sphere, config.capacity]);

  useEffect(() => {
    if (!meshRef.current || config.isLocal) return;

    const geometry = meshRef.current.geometry;

    geometry.setAttribute(
      "instanceEmitWorldPosition",
      new InstancedBufferAttribute(new Float32Array(config.capacity * 3), 3),
    );
  }, [meshRef, config.capacity, config.isLocal]);

  return null;
};
